import React from "react";
import {
  Box,
  Typography,
  CardContent,
  Divider,
  LinearProgress,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { TermReportStatsDto } from "../../../shared/dtos/studentTermReport/TermReportStatsDto";

const formatGrade = (gradeStr: string): string => {
  if (!gradeStr) return "";
  if (gradeStr === "F") return "F";
  return gradeStr.replace("_PLUS", "+").replace("_MINUS", "-").replace("_", "");
};

interface StudentTermReportWidgetProps {
  data: TermReportStatsDto;
}

const StudentTermReportWidget: React.FC<StudentTermReportWidgetProps> = ({
  data,
}) => {
  const meta = getEntityMeta("STUDENT_TERM_REPORT");
  const Icon = meta.icon;

  const chartData =
    data.gradeDistribution?.map((item) => ({
      grade: formatGrade(String(item.grade)),
      count: item.count,
    })) || [];

  const publishRate =
    data.totalReports > 0
      ? (data.publishedReports / data.totalReports) * 100
      : 0;

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Reports: {data.totalReports}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ my: 1 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
          >
            <Typography variant="body2">Published</Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {data.publishedReports} / {data.totalReports}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={publishRate}
            color={publishRate === 100 ? "success" : "primary"}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            display: "block",
            textAlign: "center",
            mb: 1,
          }}
        >
          Overall Grade Distribution
        </Typography>

        {chartData.length > 0 ? (
          <Box sx={{ flexGrow: 1, width: "100%", minHeight: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="grade"
                  tick={{ fontSize: 12, fontWeight: "bold" }}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip cursor={{ fill: "transparent" }} />
                <Bar dataKey="count" fill={meta.color} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 180,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontStyle: "italic" }}
            >
              No grade distribution data available.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default StudentTermReportWidget;
